import Excel, { Row } from 'exceljs';

import {
  cartesianColumnToExcel,
  excelToCartesian,
} from '@/common/components/controls/table/Spreadsheet/ExcelCoordinatesHelper';
import { ExcelParser } from '@/common/components/controls/table/Spreadsheet/ExcelParser';
import TableColumn from '@/common/components/controls/table/TableColumn';
import FormControl from '@/common/components/generic-form/form-controls/FormControl';

export type SheetToModelMapping<T> = {
  [key in keyof T]: string | string[];
};

export enum CellType {
  String,
  Number,
  Boolean,
}

type ParsedCellType<T extends CellType> = T extends CellType.String
  ? string
  : T extends CellType.Number
    ? number
    : boolean;

export abstract class SpreadSheetModelParser<Model> {
  public abstract modelName: string;
  public abstract readonly templatePath: string;
  public abstract readonly worksheetName: string;
  public abstract readonly tableColumns: Record<string, TableColumn>;
  protected abstract readonly headerRange: { from: string; to: string };
  protected abstract excelMapping: SheetToModelMapping<Partial<Model>>;

  /**
   * Compare the uploaded worksheet with the template worksheet
   */
  public async validateWorksheet(worksheet: Excel.Worksheet): Promise<string | null> {
    const workbookRequest = await fetch(this.templatePath);
    const workbookBuffer = await workbookRequest.arrayBuffer();
    const templateWorkbook = new Excel.Workbook();
    await templateWorkbook.xlsx.load(workbookBuffer);

    const templateWorksheet = templateWorkbook.worksheets.find((x) => x.name === this.worksheetName);

    const startCell = excelToCartesian(this.headerRange.from);
    const endCell = excelToCartesian(this.headerRange.to);
    const excelRow = startCell[1] + 1;
    const templateRow = templateWorksheet.getRow(excelRow);
    const uploadedRow = worksheet.getRow(excelRow);
    for (let column = startCell[0]; column <= endCell[0]; column++) {
      const excelColumn = cartesianColumnToExcel(column);
      const templateCell = templateRow.getCell(excelColumn);
      const uploadedCell = uploadedRow.getCell(excelColumn);
      if (templateCell.toString() !== uploadedCell.toString()) {
        return 'The uploaded file does not match the expected template';
      }
    }
    return null;
  }

  /**
   * Get the data rows from the worksheet
   */
  public getDataRows(worksheet: Excel.Worksheet): Row[] {
    const [, headerRow] = excelToCartesian(this.headerRange.from);
    const firstDataRow = headerRow + 1;
    const lastDataRow = worksheet.lastRow.number + 1;
    const rows = [];
    for (let rowId = firstDataRow; rowId <= lastDataRow; rowId++) {
      const excelRowId = rowId + 1;
      const row = worksheet.getRow(excelRowId);
      if (row.hasValues) {
        rows.push(row);
      }
    }
    return rows;
  }

  protected parseCell<T extends CellType>(
    row: Excel.Row,
    column: keyof typeof this.excelMapping,
    type: T,
  ): ParsedCellType<T> {
    try {
      const columnValue = this.excelMapping[column];
      if (Array.isArray(columnValue)) {
        return null;
      }
      const cell = row.getCell(columnValue);

      switch (type) {
        case CellType.String:
          return ExcelParser.parseString(cell) as ParsedCellType<T>;
        case CellType.Number:
          return ExcelParser.parseNumber(cell) as ParsedCellType<T>;
        case CellType.Boolean:
          return ExcelParser.parseBoolean(cell) as ParsedCellType<T>;
        default:
          return ExcelParser.parseRaw(cell) as ParsedCellType<T>;
      }
    } catch {
      return null;
    }
  }

  public abstract parseRow(row: Excel.Row): Promise<FormControl<Model>> | FormControl<Model>;

  public abstract createEntity(entity: Model): Promise<void>;
}
